import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  FaEnvelope,
  FaEye,
  FaReply,
  FaTrash,
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  fetchInquiries,
  deleteInquiry,
  updateInquiryStatus,
} from "../../store/slices/contactSlice";

const AdminInquiries = () => {
  const dispatch = useDispatch();
  const { inquiries, loading, error, pagination } = useSelector((state) => state.contact);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(
      fetchInquiries({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: selectedStatus,
      }),
    );
  }, [dispatch, currentPage, searchTerm, selectedStatus]);

  const handleDelete = async (inquiryId) => {
    if (window.confirm("Are you sure you want to delete this inquiry?")) {
      await dispatch(deleteInquiry(inquiryId));
      dispatch(
        fetchInquiries({
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          status: selectedStatus,
        }),
      );
    }
  };

  const handleMarkResponded = async (inquiryId) => {
    await dispatch(updateInquiryStatus({ id: inquiryId, status: "responded" }));
    dispatch(
      fetchInquiries({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: selectedStatus,
      }),
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(
      fetchInquiries({
        page: 1,
        limit: itemsPerPage,
        search: searchTerm,
        status: selectedStatus,
      }),
    );
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const totalPages = pagination?.totalPages || 1;

  const getStatusIcon = (status) => {
    switch (status) {
      case "new":
        return <FaExclamationTriangle className="text-yellow-500" />;
      case "responded":
        return <FaCheckCircle className="text-green-500" />;
      case "in-progress":
        return <FaClock className="text-blue-500" />;
      default:
        return <FaEnvelope className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "bg-yellow-100 text-yellow-800";
      case "responded":
        return "bg-green-100 text-green-800";
      case "in-progress":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
      <Helmet>
        <title>Inquiries - Admin</title>
      </Helmet>

      <div className="space-y-5">
        {/* Page header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Inquiries</h1>
          <p className="text-gray-500 text-sm">Respond to customer messages and manage communications</p>
        </div>

        <div>
          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search inquiries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10 w-full"
                  />
                </div>
              </form>

              <div className="flex items-center gap-2">
                <FaFilter className="text-gray-400" />
                <select
                  value={selectedStatus}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="form-select"
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="in-progress">In Progress</option>
                  <option value="responded">Responded</option>
                </select>
              </div>
            </div>
          </div>

          {/* Inquiries Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="loading w-8 h-8 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading inquiries...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <p className="text-red-600">{error}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {inquiries?.map((inquiry) => (
                        <tr key={inquiry._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                {getStatusIcon(inquiry.status)}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {inquiry.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {inquiry.email}
                                </div>
                                {inquiry.phone && (
                                  <div className="text-sm text-gray-500">
                                    {inquiry.phone}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {inquiry.subject}
                            </div>
                            <div className="text-sm text-gray-500 max-w-xs truncate">
                              {inquiry.message?.substring(0, 50)}...
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(inquiry.status)}`}
                            >
                              {inquiry.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(inquiry.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/admin/inquiries/${inquiry._id}`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Details"
                              >
                                <FaEye />
                              </Link>
                              {inquiry.status !== "responded" && (
                                <button
                                  onClick={() =>
                                    handleMarkResponded(inquiry._id)
                                  }
                                  className="text-green-600 hover:text-green-900"
                                  title="Mark as Responded"
                                >
                                  <FaReply />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(inquiry._id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="btn btn-outline"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage(Math.min(totalPages, currentPage + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="btn btn-outline"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing page{" "}
                          <span className="font-medium">{currentPage}</span> of{" "}
                          <span className="font-medium">{totalPages}</span>
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() =>
                              setCurrentPage(Math.max(1, currentPage - 1))
                            }
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            onClick={() =>
                              setCurrentPage(
                                Math.min(totalPages, currentPage + 1),
                              )
                            }
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <FaChevronRight />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminInquiries;
